class ModernUtilities {
  // Replace $.each() with native forEach equivalent
  static each(collection, callback) {
    if (Array.isArray(collection)) {
      collection.forEach((item, index) => callback(index, item));
    } else if (collection && typeof collection === 'object') {
      Object.keys(collection).forEach(key => callback(key, collection[key]));
    }
    return collection;
  }

  // Replace $.extend() with native Object.assign equivalent
  static extend(target = {}, ...sources) {
    return Object.assign(target, ...sources);
  }

  // Replace $.proxy() with native bind equivalent  
  static proxy(fn, context) {
    if (typeof fn === 'string') {
      return context[fn].bind(context);
    }
    return fn.bind(context);
  }

  // Replace $.ajax() with modern fetch API
  static ajax(options = {}) {
    const {
      url,
      method = 'GET',
      data,
      dataType = 'json',
      success,
      error,
      complete,
      headers = {}
    } = options;

    // Handle Rails CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    // Prepare request options
    const fetchOptions = {
      method,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...headers
      }
    };

    // Handle data based on content type
    if (data) {
      if (data instanceof FormData) {
        fetchOptions.body = data;
      } else if (typeof data === 'string') {
        fetchOptions.body = data;
        fetchOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
      } else {
        // Serialize object to form data for Rails compatibility
        const formData = new URLSearchParams();
        this._serializeObject(data, formData);
        fetchOptions.body = formData;
        fetchOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
    }

    return fetch(url, fetchOptions)
      .then(response => {
        const promise = dataType === 'json' ? response.json() : 
                       dataType === 'text' ? response.text() : 
                       dataType === 'script' ? response.text() : response.json();
        
        return promise.then(data => {
          if (response.ok) {
            if (dataType === 'script') {
              // Safer script execution without eval()
              // Create a script element and append it to the document
              const script = document.createElement('script');
              script.textContent = data;
              document.head.appendChild(script);
              document.head.removeChild(script);
            }
            success?.(data, 'success', response);
            return data;
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        });
      })
      .catch(err => {
        error?.(err, 'error', err.message);
        throw err;
      })
      .finally(() => {
        complete?.();
      });
  }

  // Convenience method for GET requests
  static get(url, data, success, dataType = 'json') {
    if (typeof data === 'function') {
      success = data;
      data = null;
    }
    
    // Add data as query parameters for GET
    let fullUrl = url;
    if (data) {
      const params = new URLSearchParams();
      this._serializeObject(data, params);
      fullUrl += (url.includes('?') ? '&' : '?') + params.toString();
    }

    return this.ajax({
      url: fullUrl,
      method: 'GET',
      dataType,
      success
    });
  }

  // Convenience method for POST requests
  static post(url, data, success, dataType = 'json') {
    if (typeof data === 'function') {
      success = data;
      data = null;
    }

    return this.ajax({
      url,
      method: 'POST',
      data,
      dataType,
      success
    });
  }

  // Helper to serialize nested objects for Rails
  static _serializeObject(obj, formData, parentKey = '') {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;
      
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) {
        this._serializeObject(value, formData, formKey);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          const arrayKey = `${formKey}[${index}]`;
          if (item && typeof item === 'object') {
            this._serializeObject(item, formData, arrayKey);
          } else {
            formData.append(arrayKey, item);
          }
        });
      } else {
        formData.append(formKey, value);
      }
    });
  }

  // Document ready replacement
  static ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  // Replace $(function() {}) pattern
  static domReady(callback) {
    return this.ready(callback);
  }
}

// Make utilities available globally to replace jQuery utilities
window.ModernUtilities = ModernUtilities;

// Provide shorthand aliases for common patterns
window.forEach = ModernUtilities.each;
window.extend = ModernUtilities.extend;
window.ajax = ModernUtilities.ajax;
window.ready = ModernUtilities.ready;